import React, { useCallback, useContext, useMemo } from 'react';
import { DebounceInput } from './DebounceInput';
import styled from 'styled-components';
import { InputProps, InvertedInput } from '../../../input/Input';
import { storage } from '../../../../utils/storage';
import { AddressSelectionContext } from '../../AddressSelectionProvider';
import { Primary } from '../../../button';
import LocationErrorIcon from '@mui/icons-material/GpsOff';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { Icon } from '../../../../icons/Icon';
import { useCustomerLocation } from '../../useCustomerLocation';
import { ThreeDots } from 'svg-loaders-react';
import { StyledSteppedContainer } from '../../DeliveryAddressSelection';
import { analyticsEvents } from '../../../../utils/analytics/analytics';
import { LocationSettingsButton } from '../../../restaurants/LocationSettingsButton';
import { typography } from '../../../../theme';
import { colors } from '../../../../theme/color';

type RestaurantSearchProps = {
  // Address input placeholder
  placeholder?: string;
};

const SearchInputField: React.FC<InputProps> = (props) => {
  return <StyledInput {...props} placeholder="Paikkakunta tai osoitteesi" label={undefined} />;
};

export const RestaurantSearch: React.FC<RestaurantSearchProps> = ({ placeholder }) => {
  const [shouldHideError, setShouldHideError] = React.useState(false);
  const { searchQuery, setSearchQuery, setUserLocation } = useContext(AddressSelectionContext);
  const { getUserLocation, locationError, locationLoading, isLocationPermissionsMissing } = useCustomerLocation();

  const locateMePressed = useCallback(async () => {
    setSearchQuery('');

    const userLocation = await getUserLocation();
    setShouldHideError(false);

    if (!userLocation) {
      analyticsEvents.locateButtonClick('error');
      return;
    }

    const { latitude, longitude } = userLocation;
    if (latitude && longitude) {
      setUserLocation({ latitude: parseFloat(latitude), longitude: parseFloat(longitude) });
    }
    const query = `${userLocation.streetAddress}, ${userLocation.city}`;
    setSearchQuery(query);
    storage.setItem('pickup_search_query', query);
    analyticsEvents.locateButtonClick('success');
  }, [getUserLocation, setSearchQuery, setUserLocation]);

  const LocateMeButton: React.ReactNode = useMemo(() => {
    if (locationLoading) {
      return (
        <StyledLocateMeButton width="text" aria-label="Haetaan sijaintia">
          <StyledThreeDots />
        </StyledLocateMeButton>
      );
    }

    if (locationError || isLocationPermissionsMissing) {
      return (
        <>
          <StyledLocateMeButton
            width="text"
            onClick={() => locateMePressed()}
            aria-label="Hae nykyinen sijainti"
          >
            <Icon width={16} height={16} aria-hidden={true}>
              <LocationErrorIcon />
            </Icon>
            <span>Ei onnistunut</span>
          </StyledLocateMeButton>
        </>
      );
    }

    return (
      <StyledLocateMeButton
        width="text"
        onClick={() => locateMePressed()}
        aria-label="Hae nykyinen sijainti"
      >
        <Icon width={16} height={16} aria-hidden={true}>
          <MyLocationIcon />
        </Icon>
        <span>Paikanna</span>
      </StyledLocateMeButton>
    );
  }, [locationLoading, locationError, isLocationPermissionsMissing, locateMePressed]);

  return (
    <StyledSteppedContainer
      primary
      stepNumber={2}
      title="Hae ravintoloita"
      fullBottomBorder={true}
      elementAfterTitle={LocateMeButton}
    >
      {isLocationPermissionsMissing && !shouldHideError && (
        <StyledErrorContainer>
          <StyledErrorText>
            <strong>Sijaintia ei voitu hakea.</strong> Salli sijainnin käyttö puhelimen asetuksista.
          </StyledErrorText>
          <LocationSettingsButton onClickAction={() => setShouldHideError(true)} />
        </StyledErrorContainer>
      )}
      <DebounceInput
        placeholder={placeholder}
        element={SearchInputField}
        debounceTimeout={500}
        value={searchQuery}
        onChange={(e) => {
          setUserLocation(null);
          setSearchQuery(e.target.value);
          storage.setItem('pickup_search_query', e.target.value);
        }}
      />
    </StyledSteppedContainer>
  );
};

const StyledInput = styled(InvertedInput)`
  & label {
    color: black;
  }

  input {
    border-color: rgba(0, 0, 0, 0.3);
  }

  max-width: none;
`;

const StyledLocateMeButton = styled(Primary)`
  min-width: 88px;
  padding: 6px 12px;
  font-size: 15px;
  background-color: transparent;
  border-color: transparent;
  color: ${({ theme }) => theme.color?.secondary};
`;

const StyledThreeDots = styled(ThreeDots)`
  height: 16px;
  width: 36px;
  margin: 1.375px 0px;
`;

const StyledErrorText = styled(typography.ParagraphSmall)`
  color: ${colors.textDefault};
  margin-bottom: 10px;
`;

const StyledErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 12px;
  background-color: ${colors.gray10};
  padding: 16px;
  margin-bottom: 10px;
`;
